/**
 * Phase 23 (Plan 23-03) — DashboardHeader.
 *
 * Sticky header above the widget grid:
 *  - Title "Operational Overview".
 *  - Last-updated timestamp formatted via `formatRelative(generatedAt)`.
 *  - Refresh button that triggers
 *    `queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] })`
 *    via the `onRefresh` callback. Disabled while `isFetching`.
 *
 * Sticky-positioning lives on the wrapper so the header survives the page
 * scroll-area (per RESEARCH §5; AppShell already wraps with the scroll outlet).
 */

import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import type { JSX } from 'react';

import { Button } from '@/components/ui/button';
import { formatRelative } from '@/lib/utils/relative-time';

export interface DashboardHeaderProps {
  readonly generatedAt: string | undefined;
  readonly isFetching: boolean;
  readonly onRefresh: () => void;
}

export function DashboardHeader({
  generatedAt,
  isFetching,
  onRefresh,
}: DashboardHeaderProps): JSX.Element {
  return (
    <div
      className="bg-background sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b py-3"
      data-testid="dashboard-header"
    >
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Operational Overview</h1>
        {generatedAt !== undefined && (
          <p
            className="text-muted-foreground text-sm"
            data-testid="dashboard-last-updated"
          >
            Last updated {formatRelative(generatedAt)}
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={onRefresh}
        disabled={isFetching}
        aria-label="Refresh dashboard"
        data-testid="dashboard-refresh-btn"
      >
        <ArrowsClockwiseIcon
          size={16}
          className={isFetching ? 'animate-spin' : undefined}
        />
        Refresh
      </Button>
    </div>
  );
}
